<template>
  <v-row>
    <v-col
      cols="12"
      sm="6"
      class="px-0 px-md-2"
    >
      <div class="d-flex align-center mb-2">
        <span class="font-weight-bold">
          {{ text.label }}
        </span>
      </div>
      <div class="text-grey mb-4 d-block d-flex flex-column">
        {{ text.description }}
        <a
          v-if="text.helpUrl"
          :href="text.helpUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ text.learnLinkLabel }}
        </a>
      </div>
    </v-col>
    <v-col
      cols="12"
      sm="6"
    >
      <v-text-field
        v-model="localProvenExpertLink"
        :placeholder="text.placeholder"
        :error-messages="errorMessages"
        :loading="mutationLoading"
        :disabled="disabled"
        variant="outlined"
        clearable
        @blur="handleBlur"
      />
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import eventBus, { eventNames } from '@/lib/eventBus'
import GET_PROVEN_EXPERT_SETTINGS from '@/modules/settings/emailSettings/queries/GetProvenExpertSettings.gql'
import UPDATE_PROVEN_EXPERT_LINK from '@/modules/settings/emailSettings/queries/UpdateProvenExpertLink.gql'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  companyId: {
    type: String,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showSuccessSnackbar: {
    type: Boolean,
    default: true,
  },
  text: {
    type: Object,
    default: () => ({
      label: '',
      description: '',
      learnLinkLabel: '',
      helpUrl: '',
      placeholder: '',
      error: {
        general: '',
        invalid: '',
      },
    }),
  },
})

const text = computed(() => {
  const defaultText = {
    label: t('settings.email.provenExpert.label'),
    description: t('settings.email.provenExpert.description'),
    learnLinkLabel: t('settings.email.provenExpert.learnLinkLabel'),
    helpUrl: t('settings.email.provenExpert.helpUrl'),
    placeholder: t('settings.email.provenExpert.placeholder'),
    error: {
      general: t('settings.email.provenExpert.error.general'),
      invalid: t('settings.email.provenExpert.error.invalid'),
    },
  }

  return {
    label: props.text.label || defaultText.label,
    description: props.text.description || defaultText.description,
    learnLinkLabel: props.text.learnLinkLabel || defaultText.learnLinkLabel,
    helpUrl: props.text.helpUrl || defaultText.helpUrl,
    placeholder: props.text.placeholder || defaultText.placeholder,
    error: {
      general: props.text.error?.general || defaultText.error.general,
      invalid: props.text.error?.invalid || defaultText.error.invalid,
    },
  }
})

type Emits = {
  (e: 'update:modelValue', value: string): void
  (e: 'blur', value: string): void
  (e: 'success', value: string): void
}
const emit = defineEmits<Emits>()

const internalError = ref('')
const serverError = ref('')
const localProvenExpertLink = ref(props.modelValue)
const originalValue = ref(props.modelValue)

watch(
  () => props.modelValue,
  (newValue) => {
    if (localProvenExpertLink.value !== newValue) {
      localProvenExpertLink.value = newValue
      originalValue.value = newValue
    }
  }
)

watch(
  localProvenExpertLink,
  (newValue) => {
    validateLink(newValue)
    if (!newValue || !internalError.value) {
      emit('update:modelValue', newValue)
    }
  },
  { immediate: true }
)

function isValidUrl(value: string) {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

function validateLink(value = localProvenExpertLink.value) {
  if (!value) {
    internalError.value = ''
    return
  }

  if (!isValidUrl(value)) {
    internalError.value = text.value.error.invalid
  } else {
    internalError.value = ''
  }
}

const {
  mutate: updateProvenExpertLink,
  loading: mutationLoading,
  error: mutationError,
} = useMutation(UPDATE_PROVEN_EXPERT_LINK)

const errorMessages = computed(() => {
  if (mutationError.value?.graphQLErrors?.[0]?.message) {
    return mutationError.value.graphQLErrors[0].message
  }
  return serverError.value || internalError.value
})

watch(mutationError, (error) => {
  if (error?.graphQLErrors?.length) {
    const msg = error.graphQLErrors[0].message || text.value.error.general
    eventBus.$emit(eventNames.SHOW_SNACKBAR, {
      color: 'error',
      text: msg,
    })
  }
})

const { result, load } = useLazyQuery(GET_PROVEN_EXPERT_SETTINGS, { companyId: props.companyId })

onMounted(() => {
  if (props.companyId) {
    load()
  }
})

watch(
  () => result.value,
  (newVal) => {
    const reviewLink = newVal?.settings?.company?.provenExpertLink
    if (reviewLink) {
      localProvenExpertLink.value = reviewLink
      originalValue.value = reviewLink
      emit('update:modelValue', reviewLink)
    }
  },
  { immediate: true }
)

const handleBlur = async () => {
  validateLink()
  if (!internalError.value) {
    serverError.value = ''
    try {
      if (props.companyId && localProvenExpertLink.value !== originalValue.value) {
        const reviewLink = localProvenExpertLink.value || ''

        await updateProvenExpertLink({
          companyId: props.companyId,
          provenExpertLink: reviewLink,
        })
        emit('success', reviewLink)
        if (props.showSuccessSnackbar) {
          eventBus.$emit(eventNames.SHOW_SNACKBAR, {
            color: 'success',
            text: t(`alerts.settings.email.proven-expert.${reviewLink ? 'success' : 'removed'}`),
          })
        }
        originalValue.value = reviewLink
        emit('blur', reviewLink)
      } else {
        emit('blur', localProvenExpertLink.value || '')
      }
    } catch (error: any) {
      Sentry.captureException(error)
      if (!error.graphQLErrors) {
        serverError.value = text.value.error.general
        eventBus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: serverError.value,
        })
      }
    }
  }
}
</script>
